import { type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthContext } from './AuthProvider'
import { Spinner } from '@/components/ui'

/**
 * ProtectedRoute Component
 * Wraps routes that require authentication.
 *
 * Based on FLOW-01-AUTHENTICATION.md
 *
 * Features:
 * - Redirects unauthenticated users to login
 * - Preserves intended destination for redirect after login
 * - Shows loading state while auth is being determined
 * - Optional admin-only protection
 * - Optional tier requirement
 */

export interface ProtectedRouteProps {
  children: ReactNode
  /** Require admin role */
  requireAdmin?: boolean
  /** Require specific plan tier */
  requireTier?: 'personal' | 'professional'
  /** Custom redirect path (default: /login) */
  redirectTo?: string
  /** Custom fallback for loading state */
  loadingFallback?: ReactNode
}

export function ProtectedRoute({
  children,
  requireAdmin = false,
  requireTier,
  redirectTo = '/login',
  loadingFallback,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, isAdmin, profile } = useAuthContext()
  const location = useLocation()

  // Show loading state while determining auth
  if (isLoading) {
    return (
      loadingFallback ?? (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <Spinner size="lg" />
        </div>
      )
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the intended destination
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname }}
        replace
      />
    )
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  // Check tier requirement
  if (requireTier && profile) {
    const tierOrder: Record<string, number> = { basic: 0, personal: 1, professional: 2 }
    const userTierLevel = tierOrder[profile.plan_tier]
    const requiredTierLevel = tierOrder[requireTier]

    if (userTierLevel < requiredTierLevel) {
      // Redirect to pricing page if tier is insufficient
      return <Navigate to="/pricing" state={{ requiredTier: requireTier }} replace />
    }
  }

  return <>{children}</>
}

/**
 * AdminRoute Component
 * Shorthand for ProtectedRoute with requireAdmin=true
 */
export interface AdminRouteProps {
  children: ReactNode
  loadingFallback?: ReactNode
}

export function AdminRoute({ children, loadingFallback }: AdminRouteProps) {
  return (
    <ProtectedRoute requireAdmin loadingFallback={loadingFallback}>
      {children}
    </ProtectedRoute>
  )
}

/**
 * GuestRoute Component
 * For routes that should only be accessible to unauthenticated users
 * (e.g., login, register)
 */
export interface GuestRouteProps {
  children: ReactNode
  /** Where to redirect authenticated users (default: /dashboard) */
  redirectTo?: string
}

export function GuestRoute({
  children,
  redirectTo = '/dashboard',
}: GuestRouteProps) {
  const { isAuthenticated, isLoading } = useAuthContext()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size="lg" />
      </div>
    )
  }

  // Redirect to dashboard (or intended destination) if already authenticated
  if (isAuthenticated) {
    const from = (location.state as { from?: string })?.from ?? redirectTo
    return <Navigate to={from} replace />
  }

  return <>{children}</>
}
